import React, { useState, useEffect, useRef } from 'react';
import { Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { CTPQuestion } from '../../types/compareThePair';

interface CTPSlideProps {
  question: CTPQuestion;
  nameA: string;
  nameB: string;
  symbolA: string;
  symbolB: string;
  logoUrlA: string | null;
  logoUrlB: string | null;
  descriptionA: string | null;
  descriptionB: string | null;
  onAnswer: (choice: 'A' | 'B') => void;
  onNext: () => void;
  onPrev: () => void;
  canGoBack: boolean;
  isRevealed: boolean;
  lastAnswer: 'A' | 'B' | null;
  wasCorrect: boolean;
  isLast: boolean;
}

// ── Animated counter hook ──────────────────────────────────
function useCountUp(target: number, duration: number, active: boolean): number {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!active) {
      setCurrent(0);
      return;
    }

    const start = performance.now();
    let raf: number;

    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setCurrent(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, active]);

  return current;
}

// Format an interpolated raw value using the pattern from the final formatted string
function formatAnimated(current: number, formatted: string): string {
  if (formatted.startsWith('$') || formatted.startsWith('-$')) {
    const abs = Math.abs(current);
    const sign = current < 0 ? '-' : '';
    if (formatted.includes('T')) return `${sign}$${(abs / 1e12).toFixed(1)}T`;
    if (formatted.includes('B')) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
    if (formatted.includes('M')) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
    return `${sign}$${Math.round(abs).toLocaleString()}`;
  }
  if (formatted.endsWith('%')) return `${(current * 100).toFixed(1)}%`;
  if (formatted.endsWith('x')) return `${current.toFixed(2)}x`;
  return String(Math.round(current));
}

// ── Bar max height in px ───────────────────────────────────
const BAR_MAX_PX = 240;

// ── Components ─────────────────────────────────────────────
const CompanyLogo: React.FC<{ logoUrl: string | null; name: string; symbol: string }> = ({
  logoUrl,
  name,
  symbol,
}) => {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-contain bg-white p-2"
      />
    );
  }
  return (
    <div
      className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-xl font-bold bg-muted text-foreground"
    >
      {symbol.slice(0, 2)}
    </div>
  );
};

const AnimatedValue: React.FC<{
  rawValue: number;
  formattedValue: string;
  color: string;
  active: boolean;
  duration?: number;
}> = ({ rawValue, formattedValue, color, active, duration = 2200 }) => {
  const animated = useCountUp(rawValue, duration, active);
  const display = active ? formatAnimated(animated, formattedValue) : '';

  return (
    <p
      className="text-lg sm:text-2xl font-bold font-mono mt-1"
      style={{ color, minHeight: '1.75rem', textShadow: '0 0 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)' }}
    >
      {display}
    </p>
  );
};

export const CTPSlide: React.FC<CTPSlideProps> = ({
  question,
  nameA,
  nameB,
  symbolA,
  symbolB,
  logoUrlA,
  logoUrlB,
  descriptionA,
  descriptionB,
  onAnswer,
  onNext,
  onPrev,
  canGoBack,
  isRevealed,
  lastAnswer,
  wasCorrect,
  isLast,
}) => {
  const [barsAnimated, setBarsAnimated] = useState(false);
  const [showCommentary, setShowCommentary] = useState(false);
  const prevIndex = useRef(question.index);

  // Reset animation state when question changes
  useEffect(() => {
    if (question.index !== prevIndex.current) {
      setBarsAnimated(false);
      setShowCommentary(false);
      prevIndex.current = question.index;
    }
  }, [question.index]);

  // Trigger bar grow after reveal (small delay so DOM renders at height 0 first)
  // Depends on question.index so it re-fires when navigating between revealed questions (review mode)
  useEffect(() => {
    if (isRevealed) {
      const barTimer = setTimeout(() => setBarsAnimated(true), 80);
      const commentaryTimer = setTimeout(() => setShowCommentary(true), 1800);
      return () => {
        clearTimeout(barTimer);
        clearTimeout(commentaryTimer);
      };
    }
  }, [isRevealed, question.index]);

  const { rawValueA, rawValueB, lowerIsBetter } = question;
  const maxVal = Math.max(Math.abs(rawValueA), Math.abs(rawValueB));

  // Bar heights in pixels (larger value = BAR_MAX_PX, smaller proportional)
  const barHeightA = maxVal > 0 ? (Math.abs(rawValueA) / maxVal) * BAR_MAX_PX : BAR_MAX_PX / 2;
  const barHeightB = maxVal > 0 ? (Math.abs(rawValueB) / maxVal) * BAR_MAX_PX : BAR_MAX_PX / 2;

  // Colors: green for the "better" side
  const aIsBetter = lowerIsBetter ? rawValueA <= rawValueB : rawValueA >= rawValueB;
  const barColorA = aIsBetter ? 'var(--ctp-correct)' : 'var(--ctp-wrong)';
  const barColorB = !aIsBetter ? 'var(--ctp-correct)' : 'var(--ctp-wrong)';

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Two-panel grid */}
      <div className="flex-1 relative grid grid-cols-2 gap-2 sm:gap-3 min-h-0">
        {/* ── Panel A ── */}
        <button
          onClick={() => !isRevealed && onAnswer('A')}
          disabled={isRevealed}
          className={`ctp-card-a relative rounded-2xl flex flex-col items-center justify-center p-3 sm:p-4 overflow-hidden transition-all ${
            !isRevealed
              ? 'cursor-pointer hover:scale-[1.01] active:scale-[0.98]'
              : 'cursor-default'
          }`}
          style={{
            border: `1px solid ${
              isRevealed && lastAnswer === 'A'
                ? wasCorrect ? 'var(--ctp-correct)' : 'var(--ctp-wrong)'
                : 'var(--ctp-card-border)'
            }`,
          }}
        >
          {/* Check / X badge */}
          {isRevealed && lastAnswer === 'A' && (
            <div
              className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center animate-fade-up"
              style={{ backgroundColor: wasCorrect ? 'var(--ctp-correct)' : 'var(--ctp-wrong)' }}
            >
              {wasCorrect ? <Check className="w-3.5 h-3.5 text-white" /> : <X className="w-3.5 h-3.5 text-white" />}
            </div>
          )}

          {/* Company info */}
          <div className={`flex flex-col items-center z-10 transition-transform duration-500 ${isRevealed ? '-translate-y-3' : ''}`}>
            <CompanyLogo logoUrl={logoUrlA} name={nameA} symbol={symbolA} />
            <p className="font-semibold text-sm sm:text-base mt-2 text-center leading-tight h-10 flex items-center" style={{ color: 'hsl(var(--foreground))' }}>
              {nameA}
            </p>
            <p className="text-xs font-mono mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {symbolA}
            </p>

            {/* Animated counter value */}
            {isRevealed && (
              <AnimatedValue
                rawValue={rawValueA}
                formattedValue={question.formattedValueA}
                color={barColorA}
                active={isRevealed}
                duration={2200}
              />
            )}

            {/* Description (pre-reveal only) */}
            {!isRevealed && descriptionA && (
              <p className="text-sm sm:text-base leading-relaxed mt-2 text-center px-3 sm:px-6 text-foreground/90">
                {descriptionA}
              </p>
            )}
          </div>

          {/* Animated bar growing from bottom */}
          {isRevealed && (
            <div className="absolute bottom-0 left-0 right-0 flex justify-center px-6 sm:px-8">
              <div
                className="w-full max-w-[160px] rounded-t-lg"
                style={{
                  height: barsAnimated ? `${barHeightA}px` : '0px',
                  backgroundColor: barColorA,
                  opacity: 0.55,
                  transition: 'height 2200ms cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
            </div>
          )}
        </button>

        {/* ── Panel B ── */}
        <button
          onClick={() => !isRevealed && onAnswer('B')}
          disabled={isRevealed}
          className={`ctp-card-b relative rounded-2xl flex flex-col items-center justify-center p-3 sm:p-4 overflow-hidden transition-all ${
            !isRevealed
              ? 'cursor-pointer hover:scale-[1.01] active:scale-[0.98]'
              : 'cursor-default'
          }`}
          style={{
            border: `1px solid ${
              isRevealed && lastAnswer === 'B'
                ? wasCorrect ? 'var(--ctp-correct)' : 'var(--ctp-wrong)'
                : 'var(--ctp-card-border)'
            }`,
          }}
        >
          {isRevealed && lastAnswer === 'B' && (
            <div
              className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center animate-fade-up"
              style={{ backgroundColor: wasCorrect ? 'var(--ctp-correct)' : 'var(--ctp-wrong)' }}
            >
              {wasCorrect ? <Check className="w-3.5 h-3.5 text-white" /> : <X className="w-3.5 h-3.5 text-white" />}
            </div>
          )}

          <div className={`flex flex-col items-center z-10 transition-transform duration-500 ${isRevealed ? '-translate-y-3' : ''}`}>
            <CompanyLogo logoUrl={logoUrlB} name={nameB} symbol={symbolB} />
            <p className="font-semibold text-sm sm:text-base mt-2 text-center leading-tight h-10 flex items-center" style={{ color: 'hsl(var(--foreground))' }}>
              {nameB}
            </p>
            <p className="text-xs font-mono mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {symbolB}
            </p>

            {isRevealed && (
              <AnimatedValue
                rawValue={rawValueB}
                formattedValue={question.formattedValueB}
                color={barColorB}
                active={isRevealed}
                duration={2200}
              />
            )}

            {!isRevealed && descriptionB && (
              <p className="text-sm sm:text-base leading-relaxed mt-2 text-center px-3 sm:px-6 text-foreground/90">
                {descriptionB}
              </p>
            )}
          </div>

          {isRevealed && (
            <div className="absolute bottom-0 left-0 right-0 flex justify-center px-6 sm:px-8">
              <div
                className="w-full max-w-[160px] rounded-t-lg"
                style={{
                  height: barsAnimated ? `${barHeightB}px` : '0px',
                  backgroundColor: barColorB,
                  opacity: 0.55,
                  transition: 'height 2200ms cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
            </div>
          )}
        </button>

        {/* Floating commentary overlay */}
        {isRevealed && question.commentary && (
          <div
            className="absolute bottom-3 left-2 right-2 z-20 flex justify-center pointer-events-none transition-opacity duration-500"
            style={{ opacity: showCommentary ? 1 : 0 }}
          >
            <div className="bg-black/85 backdrop-blur-sm rounded-xl px-4 py-3 max-w-4xl">
              <p className="text-sm sm:text-base leading-relaxed text-center text-white/90">
                {question.commentary}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom bar — always visible */}
      <div className="flex items-center pt-3 pb-1">
        {/* Back button */}
        <div className="w-10">
          {canGoBack && (
            <button
              onClick={onPrev}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80 active:scale-[0.95]"
              style={{ backgroundColor: 'hsl(var(--muted))' }}
            >
              <ChevronLeft className="w-4 h-4" style={{ color: 'hsl(var(--muted-foreground))' }} />
            </button>
          )}
        </div>

        {/* Next button — centered, greyed out until answered */}
        <div className="flex-1 flex justify-center">
          <button
            onClick={onNext}
            disabled={!isRevealed}
            className="flex items-center gap-2 px-8 py-2.5 rounded-full font-semibold text-sm transition-all"
            style={{
              backgroundColor: isRevealed ? 'var(--ctp-accent)' : 'hsl(var(--muted))',
              color: isRevealed ? '#fff' : 'hsl(var(--muted-foreground))',
              cursor: isRevealed ? 'pointer' : 'default',
            }}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Spacer for balance */}
        <div className="w-10" />
      </div>
    </div>
  );
};
